import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
    Icon,
    IconMuteEveryone
} from '../../base/icons';
import { getEffectiveRosterPermissions } from '../../base/permissions';
import {
    getMainRoomRoster,
    requestParticipantMute,
    sendToBackstage
} from '../../webinar';
import { ACTION_TRIGGER, MEDIA_STATE } from '../constants';
import { findStyledAncestor } from '../functions';

import { ParticipantItem } from './ParticipantItem';
import { RosterContextMenu } from './RosterContextMenu';
import { useModerationStyles } from './moderation/styles';
import {
    ParticipantContainer,
    SearchParticipantInput
} from './styled';

/**
 * Role ordering for the list, so the people running the meeting stay at the top
 * instead of wherever the server happened to iterate them.
 */
const ROLE_ORDER = {
    moderator: 0,
    panelist: 1,
    presenter: 2
};

const _inlineStyles = {
    _s0: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-around'
    },
    _s1: {
        maxHeight: '50vh',
        overflowY: 'auto',
        overflowX: 'hidden'
    }
};

const initialState = Object.freeze(Object.create(null));

/**
 * The people who are live in the main room, shown to a moderator who is sitting
 * backstage.
 *
 * Nothing here comes from the local participant list: backstage is a separate
 * room and shares no occupancy with the main one, so these rows — names, roles,
 * mute state, hidden state — are entirely what the server published (see
 * WEBINAR_MAIN_ROSTER). For the same reason every control on a row is a request
 * relayed through the server rather than a local conference call.
 *
 * The rows themselves are the same {@code ParticipantItem} the backstage tab
 * draws, fed from the roster instead of from redux, so both tabs of the pane
 * read as one list.
 *
 * @returns {JSX.Element}
 */
const MainRoomParticipantList = () => {
    const { t } = useTranslation();
    const { classes } = useModerationStyles();
    const dispatch = useDispatch();
    const roster = useSelector(getMainRoomRoster);
    const [ raiseContext, setRaiseContext ] = useState(initialState);
    const [ searchString, setSearchString ] = useState('');
    const isMouseOverMenu = useRef(false);

    const occupants = useMemo(() => [ ...roster ]
        .filter(occupant => !searchString
            || (occupant.name || '').toLowerCase().includes(searchString.toLowerCase()))
        .sort((a, b) => {
            const orderA = ROLE_ORDER[a.role] ?? 3;
            const orderB = ROLE_ORDER[b.role] ?? 3;

            if (orderA !== orderB) {
                return orderA - orderB;
            }

            return (a.name || '').localeCompare(b.name || '');
        }), [ roster, searchString ]);

    const lowerMenu = useCallback(() => {
        // Same enter/leave ordering problem the meeting list has: the check for
        // the pointer having moved onto the menu has to wait for the menu's own
        // enter event.
        window.queueMicrotask(() => {
            if (isMouseOverMenu.current) {
                return;
            }

            if (raiseContext !== initialState) {
                setRaiseContext(initialState);
            }
        });
    }, [ raiseContext ]);

    const toggleMenu = useCallback(occupant => e => {
        const { occupant: raisedOccupant } = raiseContext;

        if (raisedOccupant && raisedOccupant.id === occupant.id) {
            lowerMenu();
        } else {
            setRaiseContext({
                occupant,
                offsetTarget: findStyledAncestor(e.target, ParticipantContainer)
            });
        }
    }, [ lowerMenu, raiseContext ]);

    const menuEnter = useCallback(() => {
        isMouseOverMenu.current = true;
    }, []);

    const menuLeave = useCallback(() => {
        isMouseOverMenu.current = false;
        lowerMenu();
    }, [ lowerMenu ]);

    const menuSelect = useCallback(() => setRaiseContext(initialState), []);

    // There is no cross-room mute-everyone request, so this is the per-person
    // one sent to each of them. Moderators are left alone, as they are by the
    // main room's own mute-all.
    const muteAll = useCallback(() => {
        roster.forEach(occupant => {
            if (occupant.role !== 'moderator' && !occupant.audioMuted) {
                dispatch(requestParticipantMute(occupant.id, 'audio'));
            }
        });
    }, [ dispatch, roster ]);

    return (
        <>
            <SearchParticipantInput
                aria-label = { t('participantsPane.search') }
                id = 'main-room-search-input'
                onChange = { e => {
                    setSearchString(e.target.value);
                    menuSelect();
                } }
                placeholder = { t('participantsPane.search') }
                type = 'text'
                value = { searchString } />
            <div style = { _inlineStyles._s0 }>
                <button
                    className = 'smallIcon'
                    onClick = { muteAll }>
                    <Icon src = { IconMuteEveryone } />
                    <p>{t('participantsPane.actions.muteAll')}</p>
                </button>
            </div>
            { occupants.length ? (
                <div style = { _inlineStyles._s1 }>
                    { occupants.map((occupant, i) => {
                        const name = occupant.name || t('webinar.backstageUnnamed');

                        return (
                            <ParticipantItem
                                actionsTrigger = { ACTION_TRIGGER.HOVER }
                                ariaLabel = { t('toolbar.moreOptions') }
                                audioMediaState = { occupant.audioMuted
                                    ? MEDIA_STATE.MUTED : MEDIA_STATE.UNMUTED }
                                displayName = { name }
                                index = { i + 1 }
                                isHidden = { occupant.hidden === true }
                                isHighlighted = { raiseContext.occupant?.id === occupant.id }
                                isModerator = { occupant.role === 'moderator' }
                                key = { occupant.id }
                                onContextMenu = { toggleMenu(occupant) }
                                participant = { occupant }
                                participantList = { occupants }
                                permissions = { getEffectiveRosterPermissions(occupant.role) }
                                role = { occupant.role }
                                videoMuteState = { occupant.videoMuted
                                    ? MEDIA_STATE.MUTED : MEDIA_STATE.UNMUTED } />
                        );
                    }) }
                </div>
            ) : !searchString && (
                <span className = { classes.rosterEmpty }>
                    { t('webinar.mainRoomEmpty') }
                </span>
            ) }
            <RosterContextMenu
                moveAction = { sendToBackstage }
                moveLabelKey = 'participantsPane.actions.sendToBackstage'
                onEnter = { menuEnter }
                onLeave = { menuLeave }
                onSelect = { menuSelect }
                roster = { roster }
                { ...raiseContext } />
        </>
    );
};

export default MainRoomParticipantList;
